import React, { useEffect } from 'react';
import { Layout } from 'antd';
import { useRouter } from 'next/router';
import moment from 'moment';
import Bugsnag from '@bugsnag/js';
import posthog from 'posthog-js';

import { useAPI } from 'src/util/use-api';
import { NotificationSettingsProvider } from 'src/util/useNotificationSettings';
import Assistant from 'src/components/Assistant';
import {
  AssistantProvider,
  useAssistant,
} from 'src/components/Assistant/AssistantProvider';
import { buildViewContext } from 'src/components/Assistant/viewContext';
import { isAssistantEnabled } from 'src/components/Assistant/feature';

import { useWebsocket } from '../util/use-websocket';
import Topbar from './Topbar';
import SideBar from './Sidebar';
import FullScreenLoader from '../components/Loader';
import { isUnRestrictedRoute, useAuth } from '../util/use-auth';
import BillingGateBanner from '../components/BillingGateBanner';
import { bootIntercom } from './intercom';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const router = useRouter();
  const { user, isAuthenticationInitialized } = useAuth();
  const { API, authHeaderSet } = useAPI();
  const { sendToWebsocket, connected } = useWebsocket();
  const assistantEnabled = isAssistantEnabled(user);
  const [lastPageVisited, setLastPageVisited] = React.useState<string | null>(
    null
  );
  /**
   * Initialize Bugsnag user tracking when user logs in
   */
  useEffect(() => {
    if (user && process.env.NEXT_PUBLIC_BUGSNAG_API_KEY) {
      Bugsnag.setUser(
        user.external_id,
        user.username,
        `${user.first_name} ${user.last_name}`
      );
    }
  }, [user]);

  useEffect(() => {
    bootIntercom(user);

    if (
      process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN &&
      process.env.NEXT_PUBLIC_POSTHOG_HOST &&
      user
    ) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        ui_host: 'https://eu.posthog.com',
        defaults: '2026-01-30',
      });

      posthog.identify(user.external_id, {
        username: user.username,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        language: user.language,
        active_profile: user.active_profile?.name,
        is_complete_translation_enabled: user.is_complete_translation_enabled,
        is_compliance_enabled: user.is_compliance_enabled,
      });
    }
  }, [user]);

  useEffect(() => {
    if (router.pathname === '/articles/[article_id]') {
      return;
    }
    if (user?.external_id && lastPageVisited !== window.location.href) {
      // Remove token && api_key from the query
      delete router.query.token;
      delete router.query.api_key;

      API?.profilesAPI.profilesAnalyticsPageVisitsCreate({
        pageVisitRequest: {
          current_page_name: router.pathname,
          parameters: router.query,
        },
      });

      setLastPageVisited(window.location.href);
    }
  }, [
    router.query,
    router.pathname,
    API?.profilesAPI,
    user?.external_id,
    lastPageVisited,
  ]);

  if (isUnRestrictedRoute(router.pathname)) {
    return (
      <Layout
        className={`gx-app-layout full-scroll horizontal-layout full-layout`}
      >
        <Layout>
          <Layout.Content className={`gx-layout-content gx-container-wrap`}>
            <div className="gx-main-content-wrapper">{children}</div>
            {/* <BG className="-mb-1 z-10" /> */}
            <Layout.Footer>
              <div className="gx-layout-footer-content">
                ©{moment().year()} Business Radar
              </div>
            </Layout.Footer>
          </Layout.Content>
        </Layout>
      </Layout>
    );
  }

  if (!isAuthenticationInitialized || !user || !authHeaderSet) {
    return <FullScreenLoader isMain={true} />;
  }

  return (
    <ViewContextSync
      sendToWebsocket={sendToWebsocket}
      connected={connected}
      userExternalId={user.external_id}
    >
      <AuthenticatedLayout assistantEnabled={assistantEnabled}>
        {children}
      </AuthenticatedLayout>
    </ViewContextSync>
  );
};

/**
 * Push the current view context to this tab's websocket session on every
 * navigation (and when the socket (re)connects). Omitted id fields are
 * cleared server-side, so sending on all routes — including pages with no
 * company — keeps the backend's notion of "where you are" accurate for every
 * authenticated user, not only those with the assistant enabled.
 */
const ViewContextSync = ({
  sendToWebsocket,
  connected,
  userExternalId,
  children,
}: {
  sendToWebsocket: (data: unknown) => void;
  connected: boolean;
  userExternalId: string | undefined;
  children: React.ReactNode;
}) => {
  const router = useRouter();
  useEffect(() => {
    if (!userExternalId || !router.isReady || !connected) return;
    sendToWebsocket(buildViewContext(router.pathname, router.query));
  }, [
    router.asPath,
    router.isReady,
    router.pathname,
    router.query,
    connected,
    userExternalId,
    sendToWebsocket,
  ]);
  return <>{children}</>;
};

const AuthenticatedLayout = ({
  assistantEnabled,
  children,
}: {
  assistantEnabled: boolean;
  children: React.ReactNode;
}) => (
  <NotificationSettingsProvider>
    {assistantEnabled ? (
      <AssistantProvider>
        <AssistantShell>{children}</AssistantShell>
      </AssistantProvider>
    ) : (
      <AppShell>{children}</AppShell>
    )}
  </NotificationSettingsProvider>
);

/** Authenticated app chrome: sidebar, top bar, content and footer. */
const AppShell = ({ children }: { children: React.ReactNode }) => (
  <Layout className={`gx-app-layout full-scroll horizontal-layout full-layout`}>
    <SideBar />
    <Layout>
      <Topbar />
      <BillingGateBanner />
      <Layout.Content className={`gx-layout-content gx-container-wrap`}>
        <div className="gx-main-content-wrapper full-layout">{children}</div>
        <Layout.Footer style={{ marginTop: '15px' }}>
          <div className="gx-layout-footer-content">
            ©{moment().year()} Business Radar
          </div>
        </Layout.Footer>
      </Layout.Content>
    </Layout>
  </Layout>
);

/**
 * Authenticated shell. When the assistant is pinned it reserves the panel's
 * width so content reflows beside it (the panel itself stays position:fixed).
 * The inset is driven from a `bra-pinned` class + `--bra-pin-pad` CSS var on
 * <html> (see assistant.css): it pads the layout root AND makes the viewport-
 * width `.gx-container` fluid, so both the header and content reflow. The
 * padding animates with the panel easing and drops the transition mid-drag.
 */
const AssistantShell = ({ children }: { children: React.ReactNode }) => {
  const { pinned, open, width, resizing, forbidden } = useAssistant();

  useEffect(() => {
    const root = document.documentElement;
    const active = pinned && open && !forbidden;
    const apply = () => {
      // Scale the shell to fit beside the panel: (viewport − panelWidth) / viewport.
      const scale = active
        ? Math.max(0, (window.innerWidth - width) / window.innerWidth)
        : 1;
      root.classList.toggle('bra-pinned', active);
      root.classList.toggle('bra-pin-resizing', active && resizing);
      root.style.setProperty('--bra-pin-scale', String(scale));
    };
    apply();
    // A viewport resize alone doesn't rerun this effect — recalculate the
    // scale so a pinned panel doesn't keep a stale width-to-viewport ratio.
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [pinned, open, width, resizing, forbidden]);

  // Clear the global pin state if the shell unmounts (e.g. logout).
  useEffect(
    () => () => {
      const root = document.documentElement;
      root.classList.remove('bra-pinned', 'bra-pin-resizing');
      root.style.removeProperty('--bra-pin-scale');
    },
    []
  );

  return (
    <>
      {/* Scaled when pinned (see assistant.css). */}
      <AppShell>{children}</AppShell>
      {/* Sibling of the scaled shell so the fixed panel isn't scaled/mis-placed. */}
      <Assistant />
    </>
  );
};

// @ts-ignore
export default AppLayout;
